import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { MdClose } from 'react-icons/md';
import * as sessionService from '../../services/sessionService';
import RecognizeModal from '../common/RecognizeModal';
import useImpersonation from '../../hooks/useImpersonation';
import useClubVocab from '../../hooks/useClubVocab';
import useTheme from '../../context/ThemeContext';
import { useCalendar } from '../../context/CalendarContext';

// The shared attendance-marking grid — P/L/A toggles, All Present/All Absent, live count, bulk save
// via mark-bulk (which A1 mirrors into both stores). Reused as-is by the day-agenda Sessions page
// and the simple Attendance page. `session` is the underlying record; nothing here says "session"
// to the user (the header shows the class name).
//
// Visual system: Apple Air (light) / Warm Charcoal (dark). Red is an accent only. State is shown
// with small alpha tints + a thin left accent + the selected toggle — never a large opaque fill.
// The one primary button is `bg-secondary text-card` (black/white in light, cream/dark in dark).

const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

// Marked-row surface: the thin solid left accent is the main state signal; the fill is only a
// whisper of the semantic color. Dark uses ~5% (an 8% amber over near-black reads muddy brown and
// swallows the name); light keeps the slightly stronger tint that read well. Name stays full-
// strength text-secondary in both.
const ROW_STATE_DARK = {
  present: 'bg-green-500/[0.05] border-l-[3px] border-green-500',
  late:    'bg-amber-500/[0.05] border-l-[3px] border-amber-500',
  absent:  'bg-red-500/[0.05]   border-l-[3px] border-red-400',
};
const ROW_STATE_LIGHT = {
  present: 'bg-green-500/[0.08] border-l-[3px] border-green-500',
  late:    'bg-amber-500/[0.08] border-l-[3px] border-amber-500',
  absent:  'bg-red-500/[0.06]   border-l-[3px] border-red-400',
};
// Selected toggle: solid mid tone + white text (self-contained, reads in both modes).
const TOGGLE = [
  { key: 'present', label: 'P', selected: 'bg-green-600 text-white shadow-sm', title: 'Present' },
  { key: 'late',    label: 'L', selected: 'bg-amber-600 text-white shadow-sm', title: 'Late'    },
  { key: 'absent',  label: 'A', selected: 'bg-red-600 text-white shadow-sm',   title: 'Absent'  },
];
// Unselected toggle: neutral surface, muted text; hover only brightens the text — never color-on-color.
const TOGGLE_IDLE = 'bg-nav-hover text-muted hover:text-secondary';

function CountStat({ dot, n, label }) {
  return (
    <span className="flex items-center gap-1.5 text-sm text-muted">
      <span className={`w-2 h-2 rounded-full ${dot}`} />
      <span className="font-semibold text-secondary">{n}</span> {label}
    </span>
  );
}

export default function AttendanceModal({ session, onClose, onSaved }) {
  const { isReadOnly } = useImpersonation();
  const v = useClubVocab();
  const currentTheme = useTheme()?.currentTheme;
  const { formatDateNice } = useCalendar();
  const isDark = currentTheme === 'dark';
  const ROW_STATE = isDark ? ROW_STATE_DARK : ROW_STATE_LIGHT;
  // App-canonical themed primary pill (matches the "inverted" Button + AddCharge/GiveDiscount CTAs):
  // RED brand fill in dark (global CSS hovers it to primary-dark), black/white in light.
  const primaryBtn = isDark ? 'bg-primary text-white shadow-sm' : 'bg-secondary text-card';
  const [marks, setMarks]         = useState(null);
  const [recognizing, setRecognizing] = useState(null); // { _id, name } of student to recognize

  const { data: studentsData, isLoading: loadingStudents } = useQuery({
    queryKey: ['session-students', session._id],
    queryFn:  () => sessionService.getSessionStudents(session._id),
    staleTime: 30000,
  });
  const students = studentsData?.students || [];

  const { data: existingData } = useQuery({
    queryKey: ['session-attendance', session._id],
    queryFn:  () => sessionService.getSessionAttendance(session._id),
    staleTime: 0,
  });
  const existing = existingData?.records || [];

  useEffect(() => {
    if (marks !== null) return;
    if (!students.length) return;
    const init = {};
    students.forEach(s => {
      const rec = existing.find(r => {
        const sid = r.studentId?._id || r.studentId;
        return sid?.toString() === s._id?.toString();
      });
      init[s._id] = rec ? (rec.status || 'present') : 'absent';
    });
    setMarks(init);
  }, [students, existing]);

  const setStatus = (id, status) => setMarks(prev => ({ ...prev, [id]: status }));
  const markAll   = (status) => {
    const next = {};
    students.forEach(s => { next[s._id] = status; });
    setMarks(next);
  };

  const counts = marks
    ? {
        present: Object.values(marks).filter(s => s === 'present').length,
        late:    Object.values(marks).filter(s => s === 'late').length,
        absent:  Object.values(marks).filter(s => s === 'absent').length,
      }
    : { present: 0, late: 0, absent: 0 };

  const { mutate: save, isPending } = useMutation({
    mutationFn: () => sessionService.markBulk(
      session._id,
      Object.entries(marks).map(([studentId, status]) => ({ studentId, status }))
    ),
    onSuccess: (data) => {
      const s = data.summary;
      toast.success(`Saved — ${s.present} present, ${s.absent} absent${s.late ? `, ${s.late} late` : ''}`);
      onSaved();
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to save attendance'),
  });

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-card">
      {/* Header — class name + date; no redundant class repetition */}
      <div className="flex items-center justify-between px-4 py-3.5 border-b border-divider flex-shrink-0">
        <div className="min-w-0">
          <h2 className="font-bold text-secondary text-base truncate">{session.className}</h2>
          <p className="text-xs text-subtle mt-0.5">
            {formatDateNice(session.date || '')}{session.startTime && ` · ${fmtTime(session.startTime)}`}
          </p>
        </div>
        <button onClick={onClose} className="text-subtle hover:text-secondary p-1 flex-shrink-0 rounded-lg transition-colors">
          <MdClose size={22} />
        </button>
      </div>

      {/* Live count — quiet dot + label */}
      <div className="flex items-center gap-4 px-4 py-2.5 border-b border-divider flex-shrink-0">
        <CountStat dot="bg-green-500" n={counts.present} label="Present" />
        <CountStat dot="bg-amber-500" n={counts.late}    label="Late" />
        <CountStat dot="bg-red-500"   n={counts.absent}  label="Absent" />
        <span className="ml-auto text-xs text-subtle">{students.length} {v.members.toLowerCase()}</span>
      </div>

      {/* Quick-select — quiet neutral chips */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-divider flex-shrink-0">
        <span className="text-xs text-subtle mr-1">Mark all:</span>
        <button onClick={() => markAll('present')} className="text-xs px-3 py-1.5 rounded-lg bg-nav-hover text-muted hover:text-secondary font-medium transition-colors">
          All Present
        </button>
        <button onClick={() => markAll('absent')} className="text-xs px-3 py-1.5 rounded-lg bg-nav-hover text-muted hover:text-secondary font-medium transition-colors">
          All Absent
        </button>
      </div>

      {/* Student list */}
      <div className="flex-1 overflow-y-auto">
        {loadingStudents && (
          <div className="text-center py-12 text-subtle text-sm">Loading {v.members.toLowerCase()}…</div>
        )}
        {!loadingStudents && !students.length && (
          <div className="text-center py-12 text-subtle text-sm">
            {session.classId
              ? `No enrolled ${v.members.toLowerCase()} found for this class`
              : `No active ${v.members.toLowerCase()} found in this club`}
          </div>
        )}

        {students.map(s => {
          const status = marks?.[s._id] || 'absent';
          return (
            <div
              key={s._id}
              className={`flex items-center gap-3 px-4 py-3 min-h-[56px] border-b border-divider transition-colors ${ROW_STATE[status]}`}
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 bg-nav-hover text-subtle">
                {(s.name || '?')[0].toUpperCase()}
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-secondary truncate">{s.name}</p>
                {s.currentGrade && (
                  <span className="inline-block text-[11px] px-2 py-0.5 rounded-full mt-0.5 bg-nav-hover text-subtle">
                    {s.currentGrade}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-1.5 flex-shrink-0">
                {TOGGLE.map(t => (
                  <button
                    key={t.key}
                    title={t.title}
                    onClick={() => setStatus(s._id, t.key)}
                    className={`w-11 h-11 rounded-xl text-sm font-bold transition-all active:scale-95
                      ${status === t.key ? t.selected : TOGGLE_IDLE}`}
                  >
                    {t.label}
                  </button>
                ))}
                {(status === 'present' || status === 'late') && (
                  <button
                    title="Award recognition badge (+100 XP)"
                    onClick={() => setRecognizing({ _id: s._id, name: s.name })}
                    className="w-10 h-10 rounded-xl text-base flex items-center justify-center text-subtle hover:text-purple-500 hover:bg-purple-500/10 transition-all active:scale-95"
                  >
                    🎖️
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Save — sticky footer on an elevated bar (dark) so the button doesn't float; safe-area pad */}
      <div
        className={`flex-shrink-0 px-4 pt-4 border-t border-divider ${isDark ? 'bg-nav-hover' : 'bg-card'}`}
        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      >
        <button
          onClick={() => save()}
          disabled={isPending || !marks || !students.length || isReadOnly}
          title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
          className={`w-full py-3.5 rounded-full font-semibold text-sm ${primaryBtn} hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200`}
        >
          {isPending
            ? 'Saving…'
            : `Save Attendance · ${counts.present}P · ${counts.late}L · ${counts.absent}A`
          }
        </button>
        {counts.present > 0 && (
          <p className="text-center text-[11px] text-subtle mt-2">
            Tap 🎖️ beside any present {v.member.toLowerCase()} to award a recognition badge
          </p>
        )}
      </div>

      {recognizing && (
        <RecognizeModal
          studentProfileId={recognizing._id}
          studentName={recognizing.name}
          onClose={() => setRecognizing(null)}
        />
      )}
    </div>
  );
}
